import { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { MessageCircle, Wifi, RefreshCw, QrCode, Phone, CheckCircle2, XCircle, Loader2, Smartphone, LogOut, AlertTriangle } from 'lucide-react';

interface BotStatus {
  connected: boolean;
  info?: {
    pushname: string;
    wid: { user: string; _serialized: string };
    me: { user: string };
    platform: string;
  };
}

const T = {
  title: "إدارة بوت واتس أب",
  subtitle: "اتصال وإدارة بوت واتس أب الرسمي",
  connectionStatus: "حالة الاتصال",
  connected: "متصل",
  disconnected: "غير متصل",
  refresh: "تحديث الحالة",
  scanQR: "مسح QR Code",
  scanInstructions: "امسح رمز QR الأدنى من تطبيق واتس أب:",
  step1: "افتح واتس أب على هاتفك",
  step2: "اذهب إلى الإعدادات > الأجهزة المرتبطة",
  step3: "اضغط على 'ربط جهاز'",
  step4: "امسح رمز QR الأدنى",
  connectedPhone: "الرقم المتصل",
  pushName: "اسم الحساب",
  platform: "المنصة",
  loading: "جاري التحميل...",
  testMessage: "إرسال رسالة اختبار",
  sendTest: "إرسال اختبار",
  logout: "تسجيل الخروج",
  logoutConfirm: "هل أنت متأكد من تسجيل الخروج؟ سيتم قطع اتصال الرقم الحالي تماماً."
};

export default function WhatsAppBotPage() {
  const [status, setStatus] = useState<BotStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [qrImage, setQrImage] = useState<string | null>(null);
  const [message, setMessage] = useState<{type: 'success' | 'error', text: string} | null>(null);
  const [testPhone, setTestPhone] = useState('');
  const [testMessageText, setTestMessageText] = useState('رسالة اختبار من Target Express');
  const [sending, setSending] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);

  useEffect(() => {
    loadStatus();
    loadQR();
    const interval = setInterval(() => {
      loadStatus();
      if (!status?.connected) loadQR();
    }, 5000);
    return () => clearInterval(interval);
  }, [status?.connected]);

  const loadStatus = async () => {
    try {
      const res = await api.get('/api/whatsapp-bot/status');
      const data = res.data;
      setStatus({
        connected: data.connected || false,
        info: data.info
      });
    } catch (e) {
      console.error(e);
    } finally { setLoading(false); }
  };

  const loadQR = async () => {
    try {
      const res = await api.get('/api/whatsapp-bot/qr');
      const data = res.data;
      if (data.qr) {
        setQrImage(data.qr);
      }
    } catch (e) { 
      console.error('Error loading QR:', e); 
    }
  };

  const handleLogout = async () => {
    if (!window.confirm(T.logoutConfirm)) return;
    
    setLoggingOut(true);
    try {
      const res = await api.post('/api/whatsapp-bot/logout', {});
      const data = res.data;
      
      if (data.success) {
        setMessage({ type: 'success', text: "تم تسجيل الخروج بنجاح" });
        setStatus({ connected: false, info: undefined });
        setQrImage(null);
        setTimeout(() => {
          loadStatus();
          loadQR();
          setMessage(null);
        }, 2000);
      } else {
        setMessage({ type: 'error', text: data.message || "فشل تسجيل الخروج" });
      }
    } catch (e) {
      setMessage({ type: 'error', text: "حدث خطأ أثناء تسجيل الخروج" });
    } finally {
      setLoggingOut(false);
    }
  };

  const handleSendTest = async () => {
    if (!testPhone) {
      setMessage({ type: 'error', text: "أدخل رقم الهاتف" });
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/api/whatsapp-bot/send', { phoneNumber: testPhone, message: testMessageText });
      const data = res.data;
      setMessage({ 
        type: data.success !== false ? 'success' : 'error', 
        text: data.success !== false ? "تم إرسال الرسالة بنجاح" : (data.error || "فشل الإرسال")
      });
      setTimeout(() => setMessage(null), 5000);
    } catch (e) {
      setMessage({ type: 'error', text: "حدث خطأ" });
    } finally { setSending(false); }
  };

  if (loading) {
    return (
      <div className="p-12 text-center">
        <Loader2 className="w-12 h-12 animate-spin text-emerald-600 mx-auto" />
        <p className="text-gray-500 mt-4">{T.loading}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
          <div className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl flex items-center justify-center shadow-lg">
            <MessageCircle className="w-6 h-6 text-white" />
          </div>
          <div>
            <span>{T.title}</span>
            <p className="text-sm text-gray-500 font-normal mt-1">{T.subtitle}</p>
          </div>
        </h1>
        <div className="flex gap-2">
          <Button onClick={() => { loadStatus(); loadQR(); }} variant="outline" className="border-emerald-500 text-emerald-700">
            <RefreshCw className="w-4 h-4 ml-2" />
            {T.refresh}
          </Button>
        </div>
      </div>

      {message && (
        <div className={`p-4 rounded-xl border ${message.type === 'success' ? 'bg-green-50 border-green-200 text-green-800' : 'bg-red-50 border-red-200 text-red-800'}`}>
          {message.text}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
              <Wifi className="w-5 h-5 text-emerald-600" />
              {T.connectionStatus}
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
                <span className="text-gray-700">{T.connectionStatus}</span>
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border ${status?.connected ? 'bg-green-50 text-green-700 border-green-200' : 'bg-red-50 text-red-700 border-red-200'}`}>
                  {status?.connected ? <CheckCircle2 className="w-4 h-4" /> : <XCircle className="w-4 h-4" />}
                  {status?.connected ? T.connected : T.disconnected}
                </span>
              </div>

              {status?.connected && status?.info ? (
                <>
                  <div className="flex items-center justify-between p-4 bg-emerald-50 rounded-xl border border-emerald-200">
                    <span className="text-gray-700 flex items-center gap-2"><Phone className="w-4 h-4" />{T.connectedPhone}</span>
                    <span className="font-mono font-bold text-emerald-700">+{status.info.wid.user}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-blue-50 rounded-xl border border-blue-200">
                    <span className="text-gray-700 flex items-center gap-2"><Smartphone className="w-4 h-4" />{T.pushName}</span>
                    <span className="font-semibold text-blue-700">{status.info.pushname}</span>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-purple-50 rounded-xl border border-purple-200">
                    <span className="text-gray-700">{T.platform}</span>
                    <span className="font-semibold text-purple-700 uppercase">{status.info.platform}</span>
                  </div>
                  
                  <Button 
                    onClick={handleLogout} 
                    disabled={loggingOut}
                    variant="destructive" 
                    className="w-full mt-4 bg-red-50 text-red-600 border border-red-200 hover:bg-red-100 hover:text-red-700"
                  >
                    {loggingOut ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <LogOut className="w-4 h-4 ml-2" />}
                    {T.logout}
                  </Button>
                </>
              ) : (
                <div className="p-4 bg-yellow-50 rounded-xl border border-yellow-200 flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 mt-0.5" />
                  <p className="text-sm text-yellow-800">البوت غير متصل حالياً. يرجى مسح رمز QR لربط رقم جديد.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-0 shadow-lg">
          <CardContent className="p-6">
            {status?.connected ? (
              <>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <MessageCircle className="w-5 h-5 text-emerald-600" />
                  {T.testMessage}
                </h2>
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">رقم الهاتف (مع كود الدولة)</label>
                    <input
                      type="text"
                      value={testPhone}
                      onChange={(e) => setTestPhone(e.target.value)}
                      placeholder="201000000000"
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-700 mb-1 block">الرسالة</label>
                    <textarea
                      value={testMessageText}
                      onChange={(e) => setTestMessageText(e.target.value)}
                      rows={3}
                      className="w-full px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:border-emerald-500"
                    />
                  </div>
                  <Button 
                    onClick={handleSendTest} 
                    disabled={sending}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {sending ? <Loader2 className="w-4 h-4 ml-2 animate-spin" /> : <MessageCircle className="w-4 h-4 ml-2" />}
                    {T.sendTest}
                  </Button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-4 flex items-center gap-2">
                  <QrCode className="w-5 h-5 text-emerald-600" />
                  {T.scanQR}
                </h2>
                {qrImage ? (
                  <div className="space-y-4">
                    <p className="text-sm text-gray-600">{T.scanInstructions}</p>
                    <div className="bg-white p-6 rounded-xl border-2 border-dashed border-emerald-300 flex justify-center">
                      <img src={qrImage} alt="QR Code" className="w-64 h-64 mx-auto" />
                    </div>
                    <div className="space-y-2 bg-gray-50 p-4 rounded-xl text-sm">
                      <p className="font-semibold text-gray-900">1. {T.step1}</p>
                      <p className="font-semibold text-gray-900">2. {T.step2}</p>
                      <p className="font-semibold text-gray-900">3. {T.step3}</p>
                      <p className="font-semibold text-gray-900">4. {T.step4}</p>
                    </div>
                  </div>
                ) : (
                  <div className="p-8 text-center bg-gray-50 rounded-xl">
                    <QrCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">جاري توليد QR Code...</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
